import { DateTime } from 'luxon';
import { IconType } from 'ui-toolkit';
import { ProgressStepProps } from '../ProgressBar/ProgressBar';
import { eExternalOrderStatus } from '../../../../ui-toolkit/enums/eExternalOrderStatus';
import { eInternalOrderStatus } from '../../../../ui-toolkit/enums/eInternalOrderStatus';

const externalStatusText: { [key in eExternalOrderStatus]: string } = {
  cancelled: 'Cancelled',
  signing_confirmed: 'Signing Confirmed',
  signing_occurred: 'Signing Occurred',
  signing_scheduled: 'Signing Scheduled',
  funded: 'Funded',
  funded_and_recorded: 'Funded & Recorded',
  initial_docs_available: 'Initial Docs Available',
  new: 'New',
  policy_issued: 'Policy Issued',
  recorded: 'Recorded',
  title_report_available: 'Title Report Available',
  waiting_for_funds: 'Waiting for funds',
  clear_to_close: 'Clear to close',
};

const generatePositionProp = (status: eInternalOrderStatus) => {
  switch (status) {
    case eInternalOrderStatus.new:
      return 0;
    case eInternalOrderStatus.initial_docs_available:
    case eInternalOrderStatus.waiting_for_search:
      return 1;
    case eInternalOrderStatus.production:
      return 2;
    case eInternalOrderStatus.title_report_available:
    case eInternalOrderStatus.clearance:
    case eInternalOrderStatus.title_clear:
      return 3;
    case eInternalOrderStatus.clear_to_close:
      return 4;
    case eInternalOrderStatus.signing_scheduled:
    case eInternalOrderStatus.signing_confirmed:
      return 5;
    case eInternalOrderStatus.signing_occurred:
      return 6;
    case eInternalOrderStatus.waiting_for_funds:
    case eInternalOrderStatus.ready_to_disburse:
    case eInternalOrderStatus.funded:
    case eInternalOrderStatus.recorded:
    case eInternalOrderStatus.funded_and_recorded:
    case eInternalOrderStatus.policy_issued:
      return 7;
    case eInternalOrderStatus.complete:
      return 8;
    default:
      // FYI: eInternalOrderStatus.cancelled is not given a position
      return 0;
  }
};

export interface StatusPropsGeneratorParams {
  status: eInternalOrderStatus;
  externalStatus: eExternalOrderStatus;
  created: Date;
  signed?: Date;
  done?: Date;
}

const generateProgressBarItems = ({
  status,
  externalStatus,
  signed,
  created,
  done,
}: StatusPropsGeneratorParams): ProgressStepProps[] => {
  const date_created = DateTime.fromJSDate(created).toFormat('MMM d');
  const date_signed = signed ? DateTime.fromJSDate(signed).toFormat('MMM d') : 'TBD';
  const date_done = done ? DateTime.fromJSDate(done).toFormat('MMM d') : '';
  const subtext = externalStatusText[externalStatus];
  return [
    {
      heading: 'Opened',
      subheading: date_created,
      subtext,
    },
    {
      hideWhenInactive: true,
      heading: 'Processing',
      subheading:
        status === eInternalOrderStatus.initial_docs_available ? 'Initial Docs Available' : 'Waiting for Search',
      subtext:
        (status === eInternalOrderStatus.initial_docs_available &&
          externalStatus !== eExternalOrderStatus.initial_docs_available) ||
        status === eInternalOrderStatus.waiting_for_search
          ? subtext
          : undefined,
    },
    {
      hideWhenInactive: true,
      heading: 'Examination',
      subheading: 'Title Production',
      subtext,
    },
    {
      hideWhenInactive: true,
      heading: status === eInternalOrderStatus.title_report_available ? 'Examination' : 'Clearance',
      subheading: status === eInternalOrderStatus.title_report_available ? 'Title Report Available' : 'Clearance',
      subtext:
        (status === eInternalOrderStatus.title_report_available &&
          externalStatus !== eExternalOrderStatus.title_report_available) ||
        status === eInternalOrderStatus.clearance ||
        status === eInternalOrderStatus.title_clear
          ? subtext
          : undefined,
    },
    {
      hideWhenInactive: true,
      heading: 'Ready to Schedule',
      subheading: 'Clear to Close',
      subtext,
    },
    {
      hideWhenInactive: true,
      heading: 'Signing',
      subheading: status === eInternalOrderStatus.signing_scheduled ? 'Signing Scheduled' : 'Signing Confirmed',
      subtext:
        (status === eInternalOrderStatus.signing_scheduled &&
          externalStatus !== eExternalOrderStatus.signing_scheduled) ||
        (status === eInternalOrderStatus.signing_confirmed && externalStatus !== eExternalOrderStatus.signing_confirmed)
          ? subtext
          : undefined,
    },
    {
      icon: IconType.Party,
      heading: 'Signing Date',
      subheading: date_signed,
      subtext,
    },
    {
      heading: 'Post-Signing',
      subheading: (() => {
        switch (status) {
          case eInternalOrderStatus.waiting_for_funds:
            return 'Waiting for funds';
          case eInternalOrderStatus.ready_to_disburse:
            return 'Ready to Disburse';
          case eInternalOrderStatus.funded:
            return 'Funded';
          case eInternalOrderStatus.recorded:
            return 'Recorded';
          case eInternalOrderStatus.funded_and_recorded:
            return 'Funded and Recorded';
          case eInternalOrderStatus.policy_issued:
            return 'Policy Issued';
          default:
            return '';
        }
      })(),
      subtext:
        (status === eInternalOrderStatus.waiting_for_funds &&
          externalStatus !== eExternalOrderStatus.waiting_for_funds) ||
        status === eInternalOrderStatus.ready_to_disburse ||
        (status === eInternalOrderStatus.funded && externalStatus !== eExternalOrderStatus.funded) ||
        (status === eInternalOrderStatus.recorded && externalStatus !== eExternalOrderStatus.recorded) ||
        (status === eInternalOrderStatus.funded_and_recorded &&
          externalStatus !== eExternalOrderStatus.funded_and_recorded) ||
        (status === eInternalOrderStatus.policy_issued && externalStatus !== eExternalOrderStatus.policy_issued)
          ? subtext
          : undefined,
    },
    {
      heading: 'Complete',
      subheading: date_done,
      subtext: subtext,
    },
  ];
};

const generateProgressBarProps = (data: StatusPropsGeneratorParams) => ({
  items: generateProgressBarItems(data),
  position: generatePositionProp(data.status),
});

export default generateProgressBarProps;
